import React, { useState } from 'react';
import { Calculator } from '../../types';
import { Icon } from '../Icon';
import { CalculatorShell } from './CalculatorShell';
import { exportToPDF, shareToWhatsApp } from '../../utils/exportUtils';
import { AllCalculatorsModule } from './AllCalculatorsModule';

// Import individual calculators
import BMICalculator from './health/BMICalculator';
import TMBCalculator from './health/TMBCalculator';
import MacrosCalculator from './health/MacrosCalculator';
import DailyCalorieCalculator from './health/DailyCalorieCalculator';
import WaterIntakeCalculator from './health/WaterIntakeCalculator';

interface HealthModuleProps {
  calculator: Calculator;
  onBack: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

export const HealthModule: React.FC<HealthModuleProps> = ({
  calculator,
  onBack,
  isFavorite,
  onToggleFavorite,
}) => {
  // Rolar para o topo ao entrar na calculadora
  React.useEffect(() => {
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [calculator.slug]);

  const [exportData, setExportData] = useState<{ title: string, results: { label: string, value: string }[] } | null>(null);

  const handleExportPDF = () => {
    if (!exportData) return;
    exportToPDF({
      title: exportData.title,
      description: `Relatório gerado em ${new Date().toLocaleDateString('pt-BR')}`,
      results: exportData.results
    });
  };

  const handleShareWhatsApp = () => {
    if (!exportData) return;
    shareToWhatsApp(exportData.title, exportData.results);
  };

  const supportedSlugs = [
    'imc',
    'tmb',
    'macronutrientes',
    'gasto-calorico',
    'agua-diaria'
  ];

  if (!supportedSlugs.includes(calculator.slug)) {
    return <AllCalculatorsModule calculator={calculator} onBack={onBack} />;
  }

  const handleResultChange = (data: { label: string, value: string }[]) => {
    setExportData({ title: calculator.title, results: data });
  };

  const renderHealthCalculator = () => {
    switch (calculator.slug) {
      case 'imc':
        return <BMICalculator onResultChange={handleResultChange} />;
      case 'tmb':
        return <TMBCalculator onResultChange={handleResultChange} />;
      case 'macronutrientes':
        return <MacrosCalculator onResultChange={handleResultChange} />;
      case 'gasto-calorico':
        return <DailyCalorieCalculator onResultChange={handleResultChange} />;
      case 'agua-diaria':
        return <WaterIntakeCalculator onResultChange={handleResultChange} />;
      default:
        return null;
    }
  };

  return (
    <CalculatorShell
      calculator={calculator}
      onBack={onBack}
      isFavorite={isFavorite}
      onToggleFavorite={onToggleFavorite}
      onExportPDF={exportData ? handleExportPDF : undefined}
      onShareWhatsApp={exportData ? handleShareWhatsApp : undefined}
    >
      {renderHealthCalculator()}
    </CalculatorShell>
  );
};
